import { BusPrice } from "@/components/site/BusPrice";
import { cityPageForGemeente, isGemeenteIndexable } from "@/lib/gemeente-canonical";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CityHubLinks } from "@/components/site/CityHubLinks";
import { seoTitle } from "@/lib/seo-title";
import { Plane, ShieldCheck } from "lucide-react";

import { SearchForm } from "@/components/booking/SearchForm";
import { ExtraInternalLinks } from "@/components/site/ExtraInternalLinks";
import { FaqList, faqJsonLd } from "@/components/site/FaqList";
import { LocationImage } from "@/components/site/LocationImage";
import { CityServices } from "@/components/site/CityServices";
import { hasCityServices } from "@/lib/city-services-slugs";
import { shortName } from "@/lib/place-aliases";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Button } from "@/components/ui/button";
import { loadExtraLinks } from "@/lib/extra-links-loader";
import { hreflangLinks } from "@/lib/i18n-urls";
import { PLACES_BY_SLUG } from "@/lib/local-seo";
import { absoluteImageUrl, cityImage } from "@/lib/location-images";
import { withMetaCta } from "@/lib/meta-cta";
import { PRICE_CTA_NL } from "@/lib/price-display";
import { euro } from "@/lib/pricing";
import {
  hasSchipholPage,
  SCHIPHOL_SITE,
  schipholAnswers,
  schipholCity,
  schipholFaqs,
  schipholIntro,
  schipholMeta,
  schipholPracticalFacts,
  schipholSections,
} from "@/lib/schiphol-city";

export const Route = createFileRoute("/taxi/$plaats_/schiphol")({
  loader: async ({ params }) => {
    const place = PLACES_BY_SLUG.get(params.plaats);
    // Elke plaats met een stadspagina en een vast Schiphol-tarief heeft een
    // eigen Schiphol-routepagina.
    if (!place || !hasSchipholPage(place.slug)) throw notFound();
    const extraLinks = await loadExtraLinks(`/taxi/${place.slug}/schiphol`);
    return { place, extraLinks };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Pagina niet gevonden | Taxiritje.nl" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const c = schipholCity(loaderData.place);
    const m = schipholMeta(c);
    const path = `/taxi/${c.slug}/schiphol`;
    const img = cityImage(c.slug);
    const image = img ? absoluteImageUrl(img) : `${SCHIPHOL_SITE}/og/taxiritje-brand.jpg`;
    const description = withMetaCta(m.description, "nl");
    return {
      meta: [
        { title: seoTitle(m.title) },
        { name: "description", content: description },
        { name: "keywords", content: m.keywords },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: m.ogTitle },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: m.url },
        { property: "og:site_name", content: "Taxiritje.nl" },
        { property: "og:locale", content: "nl_NL" },
        { property: "og:image", content: image },
        { property: "og:image:alt", content: img ? img.alt : `Taxi van ${c.name} naar Schiphol` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: m.ogTitle },
        { name: "twitter:description", content: m.description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: m.url }, ...hreflangLinks(path)],
    };
  },
  component: SchipholCityPage,
  notFoundComponent: SchipholNotFound,
  errorComponent: SchipholNotFound,
});

function SchipholNotFound() {
  return (
    <SiteLayout>
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">Deze Schiphol-pagina bestaat niet</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Bereken online uw vaste prijs naar Schiphol; wij rijden in heel Nederland.
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <Button asChild>
            <Link to="/boeken">Taxi boeken</Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="/taxi-schiphol">Taxi naar Schiphol</Link>
          </Button>
        </div>
      </div>
    </SiteLayout>
  );
}

function Jsonld({ data }: { data: unknown }) {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}

function SchipholCityPage() {
  const { place, extraLinks } = Route.useLoaderData();
  const c = schipholCity(place);
  const m = schipholMeta(c);
  const intro = schipholIntro(c);
  const sections = schipholSections(c);
  const answers = schipholAnswers(c);
  const faqs = schipholFaqs(c);
  const facts = schipholPracticalFacts(c);
  const image = cityImage(c.slug);

  return (
    <SiteLayout>
      <Jsonld
        data={{
          "@context": "https://schema.org",
          "@type": "TaxiService",
          name: `Taxi ${c.name} naar Schiphol`,
          description: `Privé taxi van ${c.name} naar Schiphol met een vaste prijs vooraf, deur-tot-deur en 24/7.`,
          provider: { "@id": `${SCHIPHOL_SITE}/#organization` },
          areaServed: {
            "@type": "City",
            name: c.name,
            containedInPlace: { "@type": "AdministrativeArea", name: c.province },
          },
          serviceType: ["Luchthavenvervoer", "Privé taxi", "Taxibus", "Taxi met vaste prijs", "24/7 taxi", "Vroege en nachtelijke vluchten"],
          hoursAvailable: { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], opens: "00:00", closes: "23:59" },
          ...(c.price != null
            ? {
                offers: {
                  "@type": "Offer",
                  priceCurrency: "EUR",
                  price: c.price,
                  availability: "https://schema.org/InStock",
                  description: `Vaste prijs per privé taxi van ${c.name} naar Schiphol, tot 4 personen`,
                  url: `${SCHIPHOL_SITE}/taxi/${c.slug}/schiphol`,
                  priceSpecification: {
                    "@type": "PriceSpecification",
                    priceCurrency: "EUR",
                    price: c.price,
                    valueAddedTaxIncluded: true,
                    description: `Vaste totaalprijs per taxi van ${c.name} naar Schiphol, inclusief bagage, tolwegen en parkeerkosten`,
                  },
                },
              }
            : {}),
        }}
      />
      <Jsonld data={faqJsonLd(faqs)} />

      <div className="mx-auto max-w-5xl px-4 py-8">
        <PageBreadcrumb
          items={[
            { label: "Werkgebied", to: "/taxi" },
            { label: `Taxi ${c.name}`, to: `/taxi/${c.slug}` },
          ]}
          current="Schiphol"
        />

        <h1 className="mt-4 font-display text-2xl font-bold sm:text-3xl">{m.h1}</h1>

        {/* Prijs eerst: route → vaste prijs → boeken. */}
        <div className="card-elevated mt-5 flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {c.name} → Schiphol
            </p>
            {c.price != null ? (
              <>
                <p className="mt-1 font-display text-3xl font-bold sm:text-4xl">{euro(c.price)}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Vaste prijs per privé taxi, tot 4 personen — hetzelfde bedrag als bij het
                  afrekenen.
                </p>
                <BusPrice slug={c.slug} anchor="schiphol" />
              </>
            ) : (
              <>
                <p className="mt-1 font-display text-2xl font-bold sm:text-3xl">{PRICE_CTA_NL}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Voor deze rit rekenen wij uw vaste prijs uit op uw eigen adres.
                </p>
              </>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:w-56">
            <Button asChild size="lg">
              <Link to="/boeken">Boek uw Schiphol-taxi</Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to="/taxi/$plaats" params={{ plaats: c.slug }}>
                Taxi boeken in {c.name}
              </Link>
            </Button>
          </div>
        </div>

        <div className="float-panel mt-6 p-4 sm:p-6">
          <SearchForm
            submitLabel={`Prijs ${c.name} – Schiphol`}
            prefill={{ van: c.name, naar: "Schiphol" }}
          />
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1.55fr_1fr]">
          <div className="space-y-8">
            <section className="space-y-3">
              {intro.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 28)}
                  className="text-sm leading-relaxed text-muted-foreground"
                >
                  {paragraph}
                </p>
              ))}
              {shortName(c.name) && (
                <p className="text-sm leading-relaxed text-muted-foreground">
                  De plaatsnaam wordt vaak korter geschreven: een taxi {shortName(c.name)} naar
                  Schiphol boekt u op deze pagina, tegen dezelfde vaste prijs.
                </p>
              )}
            </section>

            <LocationImage image={image} priority />

            {hasCityServices(place.slug) && <CityServices place={place} variant="schiphol" />}

            <section className="space-y-3">
              <h2 className="font-display text-xl font-bold">
                Kort antwoord op de vragen die u nu heeft
              </h2>
              <dl className="space-y-3">
                {answers.map((a) => (
                  <div key={a.q}>
                    <dt className="text-sm font-semibold text-foreground">{a.q}</dt>
                    <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{a.a}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-xl font-bold">
                Goed om te weten over de rit {c.name} – Schiphol
              </h2>
              <dl className="grid gap-3 sm:grid-cols-2">
                {facts.map((f) => (
                  <div key={f.label}>
                    <dt className="text-sm font-semibold text-foreground">{f.label}</dt>
                    <dd className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                      {f.value}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Zoekt u een Schiphol taxi {c.name}, een taxi van {c.name} naar Schiphol of juist een
                taxi van Schiphol naar {c.name}? Dat is alle drie dezelfde rit met dezelfde vaste
                prijs. Voor ritten binnen {c.name} of naar een andere bestemming bekijkt u{" "}
                <Link
                  to="/taxi/$plaats"
                  params={{ plaats: c.slug }}
                  className="font-semibold text-foreground underline decoration-primary underline-offset-2"
                >
                  taxi boeken in {c.name}
                </Link>
                .
              </p>
            </section>

            {sections.map((s) => (
              <section key={s.heading} className="space-y-3">
                <h2 className="font-display text-xl font-bold">{s.heading}</h2>
                {s.paragraphs.map((p) => (
                  <p key={p.slice(0, 28)} className="text-sm leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
                {s.list && (
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    {s.list.map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <section className="space-y-3">
              <h2 className="font-display text-xl font-bold">
                Veelgestelde vragen over de Schiphol-taxi vanuit {c.name}
              </h2>
              <FaqList items={faqs} />
            </section>
          </div>

          <aside className="space-y-4">
            <div className="card-elevated p-5">
              <h2 className="font-display text-lg font-bold">Direct reserveren</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {c.price != null
                  ? `${euro(c.price)} per privé taxi, vooraf betaald en daarna definitief.`
                  : "U ziet uw vaste prijs voordat u betaalt."}
              </p>
              <Button asChild className="mt-4 w-full">
                <Link to="/boeken">Reserveer uw taxi naar Schiphol</Link>
              </Button>
              <p className="mt-3 inline-flex items-start gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 size-3.5 text-primary" /> Erkende chauffeurs met
                taxipas, bagage en tolwegen inbegrepen.
              </p>
            </div>

            <div className="card-elevated p-5">
              <h2 className="font-display text-lg font-bold">Handig om verder te lezen</h2>
              <ul className="mt-3 space-y-2 text-sm">
                <li>
                  <Link to="/taxi/$plaats" params={{ plaats: c.slug }} className="hover:underline">
                    Taxi {c.name}: alle bestemmingen en prijzen
                  </Link>
                </li>
                <li>
                  <Link to="/taxi-schiphol" className="hover:underline">
                    Alles over onze Schipholtaxi
                  </Link>
                </li>
                <li>
                  <Link to="/luchthaventaxi" className="hover:underline">
                    Luchthavenvervoer en andere vliegvelden
                  </Link>
                </li>
                <li>
                  <Link to="/taxibus-8-personen" className="hover:underline">
                    Taxibus voor 5 tot 8 personen
                  </Link>
                </li>
                <li>
                  <Link to="/prijzen" className="hover:underline">
                    Vaste prijzen per route
                  </Link>
                </li>
                {c.place.gemeenteSlug && isGemeenteIndexable(c.place.gemeenteSlug) ? (
                  <li>
                    <Link
                      to="/taxi/gemeente/$gemeente"
                      params={{ gemeente: c.place.gemeenteSlug }}
                      className="hover:underline"
                    >
                      Taxi in de gemeente {c.gemeenteShort}
                    </Link>
                  </li>
                ) : null}
              </ul>
            </div>

            {c.nearby.length > 0 && (
              <div className="card-elevated p-5">
                <h2 className="font-display text-lg font-bold">
                  Schiphol-taxi uit plaatsen naast {c.name}
                </h2>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {c.nearby.map((n) => (
                    <li key={n.slug} className="flex items-center gap-1.5">
                      <Plane className="size-3.5 shrink-0 text-primary" />
                      <Link
                        to="/taxi/$plaats/schiphol"
                        params={{ plaats: n.slug }}
                        className="font-medium hover:underline"
                      >
                        {n.name} naar Schiphol
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </div>
      <ExtraInternalLinks links={extraLinks} />
      <CityHubLinks />
    </SiteLayout>
  );
}
